import React from "react";
import { AuthService } from "../../services";
import { SplashScreen, ErrorScreen } from "../../components";
import {useStore} from "../../store";
import {clearUser, clearAccount, setUser} from "../../store/actions";


// Create a Context for our auth state
const AuthContext = React.createContext({})

// This is an implementation of the AuthProvider as a functional component
function AuthProvider({children}) {
    const [, dispatch] = useStore();
    const [state, setState] = React.useState({
        status: 'pending',
        error: null,
        user: null,
    })

    // Effect hook runs when component is loaded and updated
    React.useEffect(() => {

        // Get the user and set the state
        AuthService.whoAmI()
            .then((user) => {

                // Update the local state so the provider knows if we're logged in.
                setState({status: 'success', error: null, user: user || null})

                // Update the global store
                dispatch(setUser(user));

            })
            .catch(error => setState({status: 'error', error, user: null}))

    }, [dispatch])  // <- empty deps means we won't update after initial load

    /**
     * Logs in a user and adds the returned user object to the global store.
     *
     * @param userName
     * @param password
     * @param willRemember
     * @param nextUrl
     * @returns {Promise<unknown>}
     */
    const login = (userName, password, willRemember, auth) => {
        // Perform the login
        return new Promise((resolve, reject) => {
            AuthService.signInWithEmailAndPassword(userName, password, willRemember, auth)
                .then(user => {
                    //console.log('login successful, updating user in Store')
                    // Update the auth context
                    setState({status: 'success', error: null, user})

                    // Update the global store
                    dispatch(setUser(user));

                    resolve();
                })
                .catch(error => {
                    // If we're trying to sign in - pass all errors back to the login page
                    reject(error);
                })
        })
    }

    /**
     * Logs out a user and clears the user from the global store.
     */
    const logout = (history) => {

        // Go to the login page page before we log out.
        // If we don't, the router will redirect us to the login page and store
        // our current location in the state. Login will then send us back to this page.
        if (history) {
            history.push("/login");
        }

        AuthService.logout().then(() => {
            setState( { status: 'success', error: null, user: null });
            dispatch(clearUser())
            dispatch(clearAccount())
        })
    }


    // Figure out what we're going to render based on the authentication status
    let result;
    if (state.status === 'pending') {
        result = <SplashScreen/>
    } else if (state.status === 'error') {
        result = <ErrorScreen error={state.error} />
    } else {
        result = children
    }

    return (
        <AuthContext.Provider value={{
            ...state,
            login,
            logout,
        }}>
            {result}
        </AuthContext.Provider>
    )
}

/**
 * AuthProvider returns another component based on the authentication state of the user.
 * AuthService is used to retrieve the logged in user.  <SplashScreen/> is returned while the API call
 * is in flight.  A successful API call returns a user object if the user is logged in, or null if the
 * user is not logged in. An unsuccessful call returns an error.
 * If the API call succeeded AuthProvider returns it's children prop, otherwise an error is displayed.
 */
/*
class AuthProvider extends Component {
    state = {
        status: 'pending',
        error:  null,
        user:   null
    }

    componentDidMount() {
        console.log('AuthProvider mounted');

        // Get the user and set the state
        AuthService.whoAmI()
            .then(user => {
                this.setState({status: 'success', error: null, user});
            })
            .catch( (error) => {
                this.setState({status: 'error', error, user: null});
            })
    }

    // Method to set the user property
    setUser = user => {
        console.log('Auth provider calling set user')
        this.setState({user})
    }

    // Method to clear the user property
    clearUser = () => {
        this.setState({user: null})
    }

    render() {
        let result;
        if (this.state.status === 'pending') {
            result = <SplashScreen/>
        } else if (this.state.status === 'error') {
            result = <ErrorScreen error={this.state.error} />
        } else {
            result = this.props.children
        }
        return (
            <AuthContext.Provider value={{
                    ...this.state,
                    setUser   : this.setUser,
                    clearUser : this.clearUser,
                }}
            >
                {result}
            </AuthContext.Provider>
        )
    }
}
*/


export { AuthProvider, AuthContext };